import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { apiClient } from '../../utils/api-client';
import type { 
  TournamentFormData, 
  TournamentFormConfig, 
  TournamentStatus, 
  FormSection 
} from '../../types/tournament-form';

/**
 * Componente unificato per la creazione e modifica tornei
 * Sostituisce create-tournament.js e edit-tournament.js
 */
@customElement('pm-tournament-form')
export class PmTournamentForm extends LitElement {
  @property({ type: String }) mode: 'create' | 'edit' = 'create';
  @property({ type: String }) apiEndpoint = '';
  @property({ type: String }) redirectUrl = '/tournaments/';
  @property({ type: Object }) initialData: Partial<TournamentFormData> = {};
  @property({ type: Object }) statusDescriptions: Record<string, TournamentStatus> = {};

  @state() private formData: Partial<TournamentFormData> = {};
  @state() private isLoading = false;
  @state() private errors: Record<string, string> = {};
  @state() private sections: Map<string, FormSection> = new Map();

  // Riferimenti agli elementi del form
  private form?: HTMLFormElement;
  private clubAutocomplete?: any; // Per compatibilità con PadelClubAutocomplete

  static styles = css`
    :host {
      display: contents;
    }
    
    .form-section {
      transition: all 0.3s ease;
    }
    
    .form-section.hidden {
      display: none !important;
    }
    
    .form-section.fade-in {
      animation: fadeIn 0.3s ease-in;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(-10px); }
      to { opacity: 1; transform: translateY(0); }
    }
    
    .loading-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(255, 255, 255, 0.8);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.initializeFormData();
  }

  firstUpdated() {
    this.findFormElements();
    this.setupEventListeners();
    this.initializeFormSections();
    this.initializePadelClubAutocomplete();
    this.updateInitialState();
  }

  /**
   * Inizializza i dati del form
   */
  private initializeFormData() {
    this.formData = { ...this.initialData };
  }

  /**
   * Trova gli elementi del form nel DOM
   */
  private findFormElements() {
    // Trova il form principale (può essere create o edit)
    this.form = document.querySelector('form[method="post"]') as HTMLFormElement ||
               document.getElementById('tournament-form') as HTMLFormElement ||
               document.querySelector('form') as HTMLFormElement;

    if (!this.form) {
      console.warn('Tournament form not found in DOM');
    }
  }

  /**
   * Configura gli event listeners
   */
  private setupEventListeners() {
    if (!this.form) return;

    // Form submit
    this.form.addEventListener('submit', (e) => {
      // Solo intercetta se siamo in modalità AJAX
      if (this.apiEndpoint) {
        e.preventDefault();
        this.handleFormSubmit(e);
      }
    });

    // Cambiamenti nei campi del form
    this.setupFieldListeners();
  }

  /**
   * Configura i listener per i campi specifici
   */
  private setupFieldListeners() {
    // Allows refunds toggle
    const allowsRefundsCheckbox = document.getElementById('id_allows_refunds') as HTMLInputElement;
    if (allowsRefundsCheckbox) {
      allowsRefundsCheckbox.addEventListener('change', () => {
        this.toggleRefundOptions();
      });
    }

    // Prize type select
    const prizeTypeSelect = document.getElementById('id_prize_type') as HTMLSelectElement;
    if (prizeTypeSelect) {
      prizeTypeSelect.addEventListener('change', () => {
        this.togglePrizeOptions();
      });
    }

    // Prize distribution select
    const prizeDistributionSelect = document.getElementById('id_prize_distribution') as HTMLSelectElement;
    if (prizeDistributionSelect) {
      prizeDistributionSelect.addEventListener('change', () => {
        this.toggleDistributionOptions();
      });
    }

    // Status select (per edit mode)
    if (this.mode === 'edit') {
      const statusSelect = document.getElementById('id_status') as HTMLSelectElement;
      if (statusSelect) {
        statusSelect.addEventListener('change', () => {
          this.updateStatusDescription();
        });
      }
    }
  }

  /**
   * Inizializza le sezioni del form
   */
  private initializeFormSections() {
    const sectionIds = [
      'refund-options',
      'registration-prize-options', 
      'fixed-prize-options',
      'percentage-prize-options',
      'custom-distribution-options'
    ];

    sectionIds.forEach(id => {
      const element = document.getElementById(id);
      if (element) {
        this.sections.set(id, {
          id,
          element: element as HTMLElement,
          visible: false,
          dependencies: []
        });
      }
    });
  }

  /**
   * Inizializza il sistema di autocompletamento padel club
   */
  private initializePadelClubAutocomplete() {
    try {
      const locationInput = document.querySelector('#id_location') as HTMLInputElement;
      if (locationInput) {
        // Create container for the new Lit component
        const container = locationInput.parentElement;
        if (container) {
          // Create and configure the new Lit autocomplete component
          const autocompleteComponent = document.createElement('pm-padel-club-autocomplete');
          autocompleteComponent.setAttribute('selector', '#id_location');
          autocompleteComponent.setAttribute('min-chars', '2');
          autocompleteComponent.setAttribute('max-results', '8');
          autocompleteComponent.setAttribute('debounce-ms', '300');
          autocompleteComponent.setAttribute('show-map-links', 'true');
          autocompleteComponent.setAttribute('show-manual-input', 'true');
          autocompleteComponent.setAttribute('placeholder', 'Cerca padel club o città...');
          autocompleteComponent.setAttribute('no-results-text', 'Nessun club trovato');
          
          // Add to container
          container.appendChild(autocompleteComponent);
          
          this.clubAutocomplete = autocompleteComponent;
          console.log('✅ PM-PadelClubAutocomplete (Lit) initialized');
        }
      }
    } catch (error) {
      console.warn('PM-PadelClubAutocomplete initialization failed:', error);
    }
  }

  /**
   * Aggiorna lo stato iniziale del form
   */
  private updateInitialState() {
    this.toggleRefundOptions();
    this.togglePrizeOptions();
    this.toggleDistributionOptions();
    
    if (this.mode === 'edit') {
      this.updateStatusDescription();
    }
  }

  /**
   * Toggle delle opzioni di rimborso
   */
  private toggleRefundOptions() {
    const checkbox = document.getElementById('id_allows_refunds') as HTMLInputElement;
    const section = this.sections.get('refund-options');
    
    if (checkbox && section) {
      this.toggleSection('refund-options', checkbox.checked);
    }
  }

  /**
   * Toggle delle opzioni premi
   */
  private togglePrizeOptions() {
    const select = document.getElementById('id_prize_type') as HTMLSelectElement;
    if (!select) return;

    const prizeType = select.value;
    
    // Hide all prize sections
    this.toggleSection('registration-prize-options', false);
    this.toggleSection('fixed-prize-options', false);
    this.toggleSection('percentage-prize-options', false);
    
    // Show appropriate section
    switch (prizeType) {
      case 'registration_based':
        this.toggleSection('registration-prize-options', true);
        break;
      case 'fixed':
        this.toggleSection('fixed-prize-options', true);
        break;
      case 'mixed':
      case 'percentage':
        this.toggleSection('percentage-prize-options', true);
        break;
    }
  }

  /**
   * Toggle delle opzioni di distribuzione premi
   */
  private toggleDistributionOptions() {
    const select = document.getElementById('id_prize_distribution') as HTMLSelectElement;
    const section = this.sections.get('custom-distribution-options');
    
    if (select && section) {
      this.toggleSection('custom-distribution-options', select.value === 'custom');
    }
  }

  /**
   * Toggle di una sezione specifica
   */
  private toggleSection(sectionId: string, show: boolean) {
    const section = this.sections.get(sectionId);
    if (!section) return;

    section.visible = show;
    
    if (show) {
      section.element.style.display = 'flex';
      section.element.classList.add('fade-in');
      // Remove fade-in class after animation
      setTimeout(() => {
        section.element.classList.remove('fade-in');
      }, 300);
    } else {
      section.element.style.display = 'none';
    }
  }

  /**
   * Aggiorna la descrizione dello status (edit mode)
   */
  private updateStatusDescription() {
    const statusSelect = document.getElementById('id_status') as HTMLSelectElement;
    const statusDescriptionDiv = document.getElementById('status-description');
    
    if (!statusSelect || !statusDescriptionDiv) return;

    const selectedStatus = statusSelect.value;
    const statusInfo = this.statusDescriptions[selectedStatus];
    
    if (statusInfo) {
      statusDescriptionDiv.innerHTML = `
        <h6 class="text-${statusInfo.color}">
          <i class="fas fa-${statusInfo.icon} me-2"></i>${statusInfo.label}
        </h6>
        <p class="mb-0">${statusInfo.description}</p>
      `;
    }
  }

  /**
   * Mostra info club selezionato
   */
  private showSelectedClub(club: any) {
    const selectedClubInfo = document.getElementById('selectedClubInfo');
    const selectedClubName = document.getElementById('selectedClubName');
    const selectedClubAddress = document.getElementById('selectedClubAddress');
    const selectedClubMapLink = document.getElementById('selectedClubMapLink');
    
    if (selectedClubName) selectedClubName.textContent = club.name;
    if (selectedClubAddress) selectedClubAddress.textContent = club.full_address;
    if (selectedClubMapLink) {
      selectedClubMapLink.setAttribute('href', 
        `https://www.google.com/maps/search/${encodeURIComponent(club.full_address)}`);
    }
    if (selectedClubInfo) selectedClubInfo.style.display = 'block';
  }

  /**
   * Gestisce il submit del form
   */
  private async handleFormSubmit(event: Event) {
    event.preventDefault();
    
    if (!this.form || !this.apiEndpoint) return;

    const formData = new FormData(this.form);
    this.isLoading = true;
    this.errors = {};

    // UI Loading
    this.showLoadingOverlay();

    try {
      const response = await apiClient.post(this.apiEndpoint, formData);

      if (response.success) {
        this.showToast(
          response.message || `Torneo ${this.mode === 'create' ? 'creato' : 'aggiornato'} con successo`,
          'success'
        );
        
        // Redirect dopo successo
        setTimeout(() => {
          window.location.href = response.redirect_url || this.redirectUrl;
        }, 1500);
      } else {
        this.errors = response.errors || {};
        this.showFormErrors();
        this.showToast(
          response.message || `Errore nel ${this.mode === 'create' ? 'creare' : 'aggiornare'} il torneo`,
          'error'
        );
      }
    } catch (error) {
      console.error('Tournament form submission error:', error);
      this.showToast('Errore di connessione. Riprova.', 'error');
    } finally {
      this.isLoading = false;
      this.hideLoadingOverlay();
    }
  }

  /**
   * Mostra errori del form
   */
  private showFormErrors() {
    // Clear previous errors
    this.form?.querySelectorAll('.is-invalid').forEach(el => {
      el.classList.remove('is-invalid');
    });
    this.form?.querySelectorAll('.invalid-feedback').forEach(el => {
      el.remove();
    });

    // Show new errors
    Object.entries(this.errors).forEach(([field, message]) => {
      const input = this.form?.querySelector(`[name="${field}"]`) as HTMLElement;
      if (input) {
        input.classList.add('is-invalid');
        
        const feedback = document.createElement('div');
        feedback.className = 'invalid-feedback';
        feedback.textContent = message;
        input.parentNode?.appendChild(feedback);
      }
    });
  }

  /**
   * Mostra overlay di loading
   */
  private showLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'loading-overlay';
    overlay.id = 'tournament-form-loading';
    overlay.innerHTML = `
      <div class="text-center">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Caricamento...</span>
        </div>
        <div class="mt-2">
          ${this.mode === 'create' ? 'Creazione torneo...' : 'Aggiornamento torneo...'}
        </div>
      </div>
    `;
    document.body.appendChild(overlay);
  }

  /**
   * Nasconde overlay di loading
   */
  private hideLoadingOverlay() {
    const overlay = document.getElementById('tournament-form-loading');
    if (overlay) {
      overlay.remove();
    }
  }

  /**
   * Mostra toast usando il sistema globale
   */
  private showToast(message: string, variant: 'success' | 'error' | 'warning' | 'info') {
    const globalShowToast = (window as any).showToast;
    if (globalShowToast) {
      globalShowToast({
        message,
        variant: variant === 'error' ? 'danger' : variant,
        duration: variant === 'success' ? 3000 : 5000
      });
    }
  }

  render() {
    // Questo componente gestisce solo la logica
    // Il form HTML esiste già nel template Django
    return html``;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pm-tournament-form': PmTournamentForm;
  }
}